import { useMemo } from 'react';

import getCodeAndLanguage from 'ui/src/components/SyntaxHighlighter/_getCodeAndLanguage';

export const getArticleId = (title: string): string => {
  const articleWords = title?.toLowerCase().split(' ');
  const articleId = articleWords?.join('-').trim();
  return articleId;
};

/**
 * Table of contents
 */

export type TocItem = {
  id: string;
  label: string;
};

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00a0',
  hellip: '\u2026',
  ndash: '\u2013',
  mdash: '\u2014',
  lsquo: '\u2018',
  rsquo: '\u2019',
  ldquo: '\u201c',
  rdquo: '\u201d',
};

const decodeEntities = (text: string) =>
  text.replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (entity, code: string) => {
    if (code[0] !== '#') return NAMED_ENTITIES[code.toLowerCase()] ?? entity;
    const codePoint = code[1].toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
    return Number.isNaN(codePoint) ? entity : String.fromCodePoint(codePoint);
  });

const escapeAttribute = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Gives every <h2> in the post HTML a stable, unique id and returns them as table-of-contents items.
 * Ids match the ones the page has always used (getArticleId of the heading text), with -2, -3… on repeats.
 */
export function addHeadingIds(html: string): { html: string; tocItems: TocItem[] } {
  const usedIds = new Map<string, number>();
  const tocItems: TocItem[] = [];

  const withIds = html.replace(
    /<h2(\s[^>]*)?>([\s\S]*?)<\/h2>/gi,
    (heading, attributes: string | undefined, inner: string) => {
      const text = decodeEntities(inner.replace(/<[^>]+>/g, ''));
      const label = text.replace(/\s+/g, ' ').trim();
      if (!label) return heading;

      const baseId = getArticleId(text);
      const occurrence = (usedIds.get(baseId) ?? 0) + 1;
      usedIds.set(baseId, occurrence);
      const id = occurrence === 1 ? baseId : `${baseId}-${occurrence}`;
      tocItems.push({ id, label });

      const otherAttributes = (attributes ?? '').replace(/\sid\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/i, '');
      return `<h2${otherAttributes} id="${escapeAttribute(id)}">${inner}</h2>`;
    },
  );

  return { html: withIds, tocItems };
}

/**
 * Syntax Highlighter
 */

type UseBodySectionsProps = {
  formattedArticleContents: string;
};

export function useBodySections({ formattedArticleContents }: UseBodySectionsProps) {
  // Memoized so the sections keep their identity across re-renders (see PostBody)
  const bodySections = useMemo(() => {
    const sections = formattedArticleContents.split(/(<pre[^>]*>[\s\S]*?<\/pre>|<span id="banner-cta"><\/span>)/);

    return sections.map((section, index) => {
      const key = `section-${index}`;
      if (section.startsWith('<span id="banner-cta">')) return { key, banner: true };
      if (!section.startsWith('<pre')) return { key, html: section };

      const { code, language } = getCodeAndLanguage({ codeSection: section });

      return { key, code, language };
    });
  }, [formattedArticleContents]);

  return { bodySections };
}
